/**
 * Web "update ready" watcher (docs/web-updates/update-alerts.html, concept D).
 *
 * Each web export is stamped with a build id (scripts/stamp-web-build.mjs): in
 * the shell's <meta name="warcarp-build"> and in /version.json. The shell
 * hard-refreshes a stale build once at cold load (public/index.html). A tab
 * that stays open never reloads by itself: this watcher re-reads
 * /version.json when the tab regains focus and once an hour while it is
 * visible, and the app offers a manual Refresh (toast, Settings badge and row).
 *
 * Turn-aware: while it is the viewer's turn on a game board the toast waits
 * (only the badge shows); it appears once the turn ends. "Later" hides the
 * toast for that build; a newer deploy shows it again.
 *
 * Web only: native has no build meta, so `currentWebBuild()` is null and
 * nothing runs.
 */
import { Platform } from 'react-native'
import { useSyncExternalStore } from 'react'

export const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000
/** Focus can fire in bursts (tab switch + window focus); one fetch covers them. */
export const UPDATE_CHECK_MIN_GAP_MS = 5 * 1000
/**
 * Below this width the toast is a phone layout, and a phone game board shows
 * the update as a strip in the map's tip slot instead (setInlineUpdateSlot).
 */
export const UPDATE_TOAST_DESKTOP_MIN_WIDTH = 900
const REFRESH_PARAM = '_build'
const REFRESH_GUARD_KEY = 'warcarp.buildRefresh'

type State = { current: string | null; latest: string | null; snoozed: string | null; ownTurn: boolean; inlineSlot: boolean }

let state: State = { current: null, latest: null, snoozed: null, ownTurn: false, inlineSlot: false }
const listeners = new Set<() => void>()

function setState(patch: Partial<State>) {
  const next = { ...state, ...patch }
  if (next.current === state.current && next.latest === state.latest && next.snoozed === state.snoozed
    && next.ownTurn === state.ownTurn && next.inlineSlot === state.inlineSlot) return
  state = next
  listeners.forEach((fn) => fn())
}

/** The build this tab is running, or null on native / a dev server. */
export function currentWebBuild(doc: Pick<Document, 'querySelector'> | undefined = typeof document === 'undefined' ? undefined : document): string | null {
  if (Platform.OS !== 'web' || !doc) return null
  const build = doc.querySelector('meta[name="warcarp-build"]')?.getAttribute('content')
  return build && build !== 'dev' ? build : null
}

export function updateReady(s: Pick<State, 'current' | 'latest'>): boolean {
  return !!s.current && !!s.latest && s.latest !== s.current
}

/** Show the toast: an update, not snoozed for this build, and not mid-turn. */
export function showUpdateToast(s: Omit<State, 'inlineSlot'>): boolean {
  return updateReady(s) && s.snoozed !== s.latest && !s.ownTurn
}

export async function fetchLatestBuild(fetchImpl: typeof fetch = fetch, now = Date.now()): Promise<string | null> {
  try {
    const res = await fetchImpl(`/version.json?t=${now}`, { cache: 'no-store' })
    if (!res.ok) return null
    const data = (await res.json()) as { build?: unknown }
    return typeof data.build === 'string' && data.build ? data.build : null
  } catch {
    return null
  }
}

/**
 * Start watching. Returns a stop function. Checks once at start, on focus
 * and visibility, and on a timer that skips hidden tabs.
 */
export function startWebUpdateWatcher(opts: {
  doc?: Document
  win?: Window
  fetchImpl?: typeof fetch
  now?: () => number
} = {}): () => void {
  const doc = opts.doc ?? (typeof document === 'undefined' ? undefined : document)
  const win = opts.win ?? (typeof window === 'undefined' ? undefined : window)
  const current = currentWebBuild(doc)
  if (!current || !doc || !win) return () => {}
  setState({ current })
  const now = opts.now ?? Date.now
  let lastCheck = -Infinity
  let inFlight = false
  const check = async () => {
    if (inFlight || doc.visibilityState === 'hidden' || now() - lastCheck < UPDATE_CHECK_MIN_GAP_MS) return
    inFlight = true
    lastCheck = now()
    const latest = await fetchLatestBuild(opts.fetchImpl ?? win.fetch.bind(win), now())
    inFlight = false
    if (latest) setState({ latest })
  }
  const onVisible = () => { if (doc.visibilityState === 'visible') void check() }
  const onFocus = () => { void check() }
  doc.addEventListener('visibilitychange', onVisible)
  win.addEventListener('focus', onFocus)
  const timer = win.setInterval(() => { void check() }, UPDATE_CHECK_INTERVAL_MS)
  // And once now: a tab that loaded stale (the shell's refresh gave up, or a
  // deploy landed mid-load) learns about it straight away.
  void check()
  return () => {
    doc.removeEventListener('visibilitychange', onVisible)
    win.removeEventListener('focus', onFocus)
    win.clearInterval(timer)
  }
}

/** The game board reports whether it's the viewer's turn, so the toast can wait. */
export function setOwnTurnActive(active: boolean) {
  setState({ ownTurn: active })
}

/**
 * A phone game board's map strip (components/game/WebUpdateStrip) holds the
 * update while it's on screen, so the floating toast steps aside.
 */
export function setInlineUpdateSlot(mounted: boolean) {
  setState({ inlineSlot: mounted })
}

/** "Later": hide the toast until a newer build ships. The badge stays. */
export function snoozeUpdate() {
  setState({ snoozed: state.latest })
}

/**
 * Load the latest build, staying on the same page (a game reopens where it
 * was). Same cache-busted navigation as the shell's cold-load refresh; the
 * new shell strips the parameter again.
 */
export function refreshToLatest(win: Pick<Window, 'location' | 'sessionStorage'> = window) {
  const latest = state.latest ?? 'latest'
  try { win.sessionStorage.setItem(REFRESH_GUARD_KEY, latest) } catch { /* private mode */ }
  const next = new URL(win.location.href)
  next.searchParams.set(REFRESH_PARAM, latest)
  win.location.replace(next.href)
}

function subscribe(fn: () => void) {
  listeners.add(fn)
  return () => { listeners.delete(fn) }
}

const getState = () => state

/** Current store snapshot (tests and non-React callers). */
export const getWebUpdateState = getState

export function useWebUpdate() {
  const s = useSyncExternalStore(subscribe, getState, getState)
  return { updateReady: updateReady(s), showToast: showUpdateToast(s), inlineSlot: s.inlineSlot, latest: s.latest }
}

/** Test hook: reset the module store. */
export function resetWebUpdateForTests(patch: Partial<State> = {}) {
  state = { current: null, latest: null, snoozed: null, ownTurn: false, inlineSlot: false, ...patch }
  listeners.forEach((fn) => fn())
}
